import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import api from '../../../../api';
import useFlashMessage from '../../../../hooks/useFlashMessage';
import { useActionLock } from '../../../../hooks/useActionLock';
import { printPdf } from '../../../../utils/funcoes';

const vazio = () => ({ titulo: '', texto: '', dataDocumento: moment().format('YYYY-MM-DD'), localEmissao: '', incluirComissao: true });
export function useDocumentosModule({ eleicao, permissoes, bloqueado }) {
    const [lista, setLista] = useState([]), [item, setItem] = useState(vazio), [arquivo, setArquivo] = useState(null);
    const [sujo, setSujo] = useState(false), [carregando, setCarregando] = useState(false), [erro, setErro] = useState('');
    const geracao = useRef(0);
    const { isLocked, run } = useActionLock();
    const { setFlashMessage } = useFlashMessage();
    const base = `/eleicao/${eleicao.uuid}/documentos`;
    const novo = () => { setItem(vazio()); setArquivo(null); setSujo(false); };
    const carregar = async () => {
        const atual = ++geracao.current;
        setCarregando(true); setErro('');
        try { const { data } = await api.get(base); if (atual === geracao.current) setLista(data); }
        catch (e) { if (atual === geracao.current) setErro(e.response?.data?.message || e.message); }
        finally { if (atual === geracao.current) setCarregando(false); }
    };
    useEffect(() => {
        setLista([]); novo();
        if (eleicao.uuid && permissoes.autorizado) carregar();
        return () => { geracao.current += 1; };
        // A lista pertence exclusivamente à eleição aberta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eleicao.uuid, permissoes.autorizado]);
    const executar = (fn) => {
        if (bloqueado || carregando) return;
        return run(async () => { try { await fn(); } catch (e) { setFlashMessage(e.response?.data?.message || e.message, 'error'); } });
    };
    const alterar = ({ target: { name, value, type, checked } }) => {
        if (bloqueado || isLocked || item.arquivoGerado || item.arquivoAssinado || !(item.uuid ? permissoes.editar : permissoes.incluir)) return;
        setItem((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value })); setSujo(true);
    };
    const selecionar = (row) => {
        if (sujo || arquivo) { setFlashMessage('Salve ou descarte a edição atual antes de selecionar outro documento.', 'warning'); return; }
        setItem({ ...row }); setArquivo(null);
    };
    const copiar = () => {
        if (!permissoes.incluir) return;
        setItem({ ...vazio(), titulo: item.titulo, texto: item.texto, localEmissao: item.localEmissao || '', incluirComissao: item.incluirComissao });
        setArquivo(null); setSujo(true);
    };
    const modelo = () => {
        if (!permissoes.incluir || sujo || arquivo) return;
        const data = eleicao.dataEleicao ? eleicao.dataEleicao.split('-').reverse().join('/') : '[DATA DA ELEIÇÃO]';
        const prazo = eleicao.fimInscricao ? moment(eleicao.fimInscricao).format('DD/MM/YYYY [às] HH:mm') : '[PRAZO DE INSCRIÇÃO]';
        setItem({ ...vazio(), titulo: `Regras para inscrição de chapas${eleicao.dataEleicao ? ' - ' + eleicao.dataEleicao.slice(0,4) : ''}`,
            texto: `A comissão eleitoral divulga as seguintes diretrizes para ${eleicao.descricao || 'a eleição'}:\n\n1. A eleição ocorrerá em ${data}, no local ${eleicao.localEleicao || '[LOCAL DA VOTAÇÃO]'}, das ${eleicao.horaInicioEleicao || '[HORA INICIAL]'} às ${eleicao.horaFimEleicao || '[HORA FINAL]'}.\n\n2. As inscrições das chapas serão recebidas pela comissão eleitoral até ${prazo}.\n\n${eleicao.diretrizes || '[INSIRA AS DEMAIS DIRETRIZES E O TRECHO DO REGIMENTO APLICÁVEL]'}` });
        setSujo(true); setArquivo(null);
    };
    const salvar = () => executar(async () => {
        if (!(item.uuid ? permissoes.editar : permissoes.incluir)) return;
        const { titulo, texto, dataDocumento, localEmissao, incluirComissao } = item;
        const { data } = await api.post(item.uuid ? `${base}/${item.uuid}/editar` : base, { titulo, texto, dataDocumento, localEmissao, incluirComissao });
        setItem(data.documento); setSujo(false); setFlashMessage(data.message, data.type); await carregar();
    });
    const anexar = () => executar(async () => {
        if (!permissoes.editar || !item.uuid || !arquivo || sujo) return;
        const fd = new FormData(); fd.append('arquivo', arquivo);
        const { data } = await api.post(`${base}/${item.uuid}/anexar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        novo(); setFlashMessage(data.message, data.type); await carregar();
    });
    const visualizar = () => executar(async () => {
        if (!permissoes.imprimir || !item.uuid || sujo || arquivo) return;
        const { data } = await api.post(`${base}/${item.uuid}/visualizar`, {}, { responseType: 'arraybuffer' }); printPdf(data);
    });
    const emitir = () => executar(async () => {
        if (!permissoes.imprimir || !permissoes.editar || !item.uuid || sujo || arquivo) return;
        const { data } = await api.post(`${base}/${item.uuid}/emitir`);
        novo(); setFlashMessage(data.message, data.type); await carregar();
    });
    const abrir = (row, tipo) => executar(async () => {
        if (!permissoes.imprimir) return;
        const { data } = await api.get(`${base}/${row.uuid}/pdf/${tipo}`, { responseType: 'arraybuffer' }); printPdf(data);
    });
    const remover = (anexo = false) => executar(async () => {
        if (!permissoes.cancelar || !item.uuid || sujo || arquivo) return;
        if (!window.confirm(anexo ? 'Remover o PDF anexado?' : 'Excluir este documento?')) return;
        const { data } = await api.post(`${base}/${item.uuid}/${anexo ? 'remover-anexo' : 'excluir'}`);
        novo(); setFlashMessage(data.message, data.type); await carregar();
    });
    const escolherArquivo = (e) => {
        const f = e.target.files?.[0]; e.target.value = '';
        if (!f) return;
        if (f.type !== 'application/pdf' || !f.name.toLowerCase().endsWith('.pdf')) { setFlashMessage('Selecione um arquivo PDF.', 'warning'); return; }
        setArquivo(f);
    };
    return { lista, item, arquivo, sujo, carregando, erro, ocupado: isLocked || carregando || bloqueado, pendente: sujo || Boolean(arquivo),
        novo, alterar, selecionar, copiar, modelo, salvar, anexar, visualizar, emitir, abrir, remover, escolherArquivo, carregar };
}
